import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { Swords } from "lucide-react";

const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: THIS_YEAR - 2007 }, (_, i) => THIS_YEAR - i);

function TeamCard({ t, other }) {
  if (!t) return null;
  const better = (a, b, invert = false) => (invert ? a < b : a > b);
  const Stat = ({ label, val, oval, invert }) => (
    <div className="flex items-center justify-between px-3 py-2 border-b border-white/5 last:border-0">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">{label}</span>
      <span className={`font-data text-sm font-black ${oval != null && better(Number(val), Number(oval), invert) ? "text-[#22c55e]" : "text-neutral-300"}`}>{val}</span>
    </div>
  );
  return (
    <div className="card-surface rounded-xl overflow-hidden" data-testid={`cmp-team-${t.id}`}>
      <div className="bg-[#8b1526] border-b border-[#c22040]/40 px-3 py-2.5 flex items-center gap-2.5">
        {t.logo && <img src={t.logo} alt="" className="w-8 h-8 object-contain" />}
        <span className="font-display font-black uppercase tracking-wide text-sm flex-1 truncate">{t.name}</span>
        <span className="font-data text-lg font-black text-white">{t.record}</span>
      </div>
      <Stat label="Win %" val={`${t.win_pct}%`} oval={other?.win_pct} />
      <Stat label="Scored / Game" val={t.pf_avg} oval={other?.pf_avg} />
      <Stat label="Allowed / Game" val={t.pa_avg} oval={other?.pa_avg} invert />
      <Stat label="Point Diff / Game" val={t.diff} oval={other?.diff} />
      <Stat label="Streak" val={t.streak} />
      <div className="px-3 py-2.5">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1.5">Last 5 Games</div>
        <div className="flex flex-wrap gap-1.5">
          {t.last5.map((g, i) => (
            <span key={i} title={`${g.date} ${g.home ? "vs" : "@"} ${g.opp}`}
              className={`text-[10px] font-data font-black px-2 py-1 rounded border ${g.result === "W" ? "bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/40" : g.result === "L" ? "bg-red-500/15 text-red-400 border-red-500/40" : "bg-white/10 text-neutral-300 border-white/20"}`}>
              {g.result} {g.home ? "vs" : "@"} {g.opp} {g.score}
            </span>
          ))}
          {t.last5.length === 0 && <span className="text-xs text-neutral-600">No completed games for this season yet.</span>}
        </div>
      </div>
    </div>
  );
}

function PlayerCompare({ league }) {
  const [qs, setQs] = useState(["", ""]);
  const [sel, setSel] = useState([null, null]);
  const r0 = useQuery({
    queryKey: ["cmpSearch", league, qs[0]],
    queryFn: () => api.get(`/matchup/player-search?league=${league}&q=${encodeURIComponent(qs[0])}`).then((r) => r.data),
    enabled: qs[0].length >= 3,
  });
  const r1 = useQuery({
    queryKey: ["cmpSearch", league, qs[1]],
    queryFn: () => api.get(`/matchup/player-search?league=${league}&q=${encodeURIComponent(qs[1])}`).then((r) => r.data),
    enabled: qs[1].length >= 3,
  });
  const p0 = useQuery({
    queryKey: ["cmpPlayer", league, sel[0]?.id],
    queryFn: () => api.get(`/matchup/player/${sel[0].id}?league=${league}`).then((r) => r.data),
    enabled: !!sel[0]?.id,
  });
  const p1 = useQuery({
    queryKey: ["cmpPlayer", league, sel[1]?.id],
    queryFn: () => api.get(`/matchup/player/${sel[1].id}?league=${league}`).then((r) => r.data),
    enabled: !!sel[1]?.id,
  });
  const packs = [p0.data, p1.data];
  const setQ = (i, v) => setQs((q) => q.map((x, j) => (j === i ? v : x)));
  const pick = (i, p) => { setSel((s) => s.map((x, j) => (j === i ? p : x))); setQ(i, p.name); };
  const res = [r0.data?.players || [], r1.data?.players || []];
  return (
    <div className="space-y-4">
      <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#a855f7] border-b border-[#a855f7]/20 pb-1">Player vs Player</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {[0, 1].map((i) => (
          <div key={i} className="relative">
            <input value={qs[i]} onChange={(e) => setQ(i, e.target.value)} data-testid={`cmp-player-search-${i}`}
              placeholder={`Search player ${i + 1} (min 3 letters)...`}
              className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-[#a855f7]/60 focus:outline-none" />
            {qs[i].length >= 3 && res[i].length > 0 && sel[i]?.name !== qs[i] && (
              <div className="absolute z-20 mt-1 w-full card-surface border border-white/10 rounded-lg overflow-hidden">
                {res[i].map((p) => (
                  <button key={p.id} onMouseDown={() => pick(i, p)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 text-left text-sm">
                    {p.headshot && <img src={p.headshot} alt="" className="w-6 h-6 rounded-full object-cover" />}
                    <span className="flex-1 truncate">{p.name}</span><span className="text-[10px] text-neutral-500">{p.team}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {packs.map((p, i) => p?.name && (
          <div key={i} className="card-surface rounded-xl overflow-hidden" data-testid={`cmp-player-card-${i}`}>
            <div className="px-3 py-2.5 flex items-center gap-2.5 bg-black/40 border-b border-white/10">
              {p.headshot && <img src={p.headshot} alt="" className="w-10 h-10 rounded-full object-cover bg-white/5" />}
              <div className="min-w-0"><div className="font-display font-black uppercase text-sm truncate">{p.name}</div>
                <div className="text-[10px] text-neutral-500">{p.pos} · {p.team}{p.age ? ` · Age ${p.age}` : ""}</div></div>
            </div>
            {p.splits?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-data whitespace-nowrap">
                  <thead><tr className="text-neutral-500 text-[9px] uppercase tracking-wider text-left border-b border-white/10">
                    <th className="p-2 sticky left-0 bg-[#141414]">Split</th>{p.stat_names.map((n) => <th key={n} className="p-2">{n}</th>)}</tr></thead>
                  <tbody>{p.splits.map((s) => (
                    <tr key={s.label} className="border-b border-white/5 last:border-0">
                      <td className="p-2 font-sans font-semibold sticky left-0 bg-[#141414] text-neutral-300">{s.label}</td>
                      {s.stats.map((v, j) => <td key={j} className="p-2 text-neutral-300">{v}</td>)}
                    </tr>))}</tbody>
                </table>
              </div>
            ) : <div className="p-4 text-xs text-neutral-500">No stats available.</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function HeadToHead({ league, comp, go }) {
  const { data, isFetching } = useQuery({
    queryKey: ["h2h", league, comp, go?.t1, go?.t2],
    queryFn: () => api.get(`/matchup/h2h?league=${league}${comp ? `&comp=${comp}` : ""}&t1=${go.t1}&t2=${go.t2}`).then((r) => r.data),
    enabled: !!go,
    staleTime: 30 * 60 * 1000,
  });
  if (!go) return null;
  const meets = data?.meetings || [];
  const chip = (txt, tone) => (
    <span className={`text-[9px] font-data font-black px-2 py-0.5 rounded border whitespace-nowrap ${tone}`}>{txt}</span>
  );
  return (
    <div className="space-y-3" data-testid="h2h-section">
      <div className="flex flex-wrap items-center gap-3 border-b border-[#a855f7]/20 pb-1">
        <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#a855f7]">Head-to-Head — Last 10 Meetings</h2>
        {data?.summary?.text && (
          <span className="font-data text-sm font-black text-[#22c55e]" data-testid="h2h-summary">{data.summary.text}</span>
        )}
      </div>
      {isFetching && <div className="text-neutral-500 font-data text-sm animate-pulse">Digging up the history books...</div>}
      {!isFetching && meets.length === 0 && (
        <div className="card-surface rounded-xl p-6 text-center text-neutral-500 text-sm" data-testid="h2h-empty">No past meetings found between these two in the last several seasons.</div>
      )}
      <div className="space-y-1.5">
        {meets.map((m, i) => (
          <div key={m.event_id} className="card-surface rounded-xl px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`h2h-row-${i}`}>
            <span className="font-data text-[10px] text-neutral-500 w-20 shrink-0">{m.date}</span>
            <span className="font-data text-sm font-black text-neutral-200 whitespace-nowrap">
              {m.away.abbrev} {m.away.score} <span className="text-neutral-600">@</span> {m.home.abbrev} {m.home.score}
            </span>
            {chip(m.winner === "Draw" ? "Draw" : `${m.winner} won`, m.winner === "Draw" ? "bg-white/10 text-neutral-300 border-white/20" : "bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/40")}
            <span className="flex flex-wrap items-center gap-1.5 ml-auto">
              {m.covered
                ? chip(m.covered === "Push" ? `ATS push${m.spread_label ? ` (${m.spread_label})` : ""}` : `${m.covered} covered${m.spread_label ? ` (${m.spread_label})` : ""}`,
                  "bg-[#a855f7]/15 text-[#a855f7] border-[#a855f7]/40")
                : chip("No spread", "bg-white/5 text-neutral-600 border-white/10")}
              {m.ou_result
                ? chip(`${m.ou_result} ${m.total_line}`, m.ou_result === "Over" ? "bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30" : m.ou_result === "Under" ? "bg-red-500/10 text-red-400 border-red-500/30" : "bg-white/10 text-neutral-300 border-white/20")
                : chip("No total", "bg-white/5 text-neutral-600 border-white/10")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const TeamSelect = ({ teams, value, onChange, testid, placeholder }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-[#a855f7]/60 focus:outline-none min-w-[180px]">
    <option value="">{placeholder}</option>
    {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
  </select>
);

export default function TeamMatchup({ league, comp, standalone = false }) {
  const { data: teamsData } = useQuery({
    queryKey: ["cmpTeams", league, comp],
    queryFn: () => api.get(`/matchup/teams?league=${league}${comp ? `&comp=${comp}` : ""}`).then((r) => r.data),
    staleTime: 60 * 60 * 1000,
  });
  const teams = teamsData?.teams || [];
  const [t1, setT1] = useState("");
  const [t2, setT2] = useState("");
  const [season, setSeason] = useState("");
  const [go, setGo] = useState(null);
  const { data: cmp, isFetching } = useQuery({
    queryKey: ["cmp", league, comp, go],
    queryFn: () => api.get(`/matchup/compare?league=${league}${comp ? `&comp=${comp}` : ""}&t1=${go.t1}&t2=${go.t2}${go.season ? `&season=${go.season}` : ""}`).then((r) => r.data),
    enabled: !!go,
  });
  const seasonLabel = (y) => (league === "nba" || league === "nhl" ? `${y - 1}-${String(y).slice(2)}` : y);
  return (
    <div className="space-y-5">
      {standalone && <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight fade-up">Matchup <span className="gold-text">Lab</span></h1>}
      <p className="text-neutral-500 text-sm">Compare any two teams from any season they've played — record, scoring, point differential, streak and last 5 games — then stack players head to head below.</p>
      <div className="flex flex-wrap items-center gap-3" data-testid="cmp-controls">
        <TeamSelect teams={teams} value={t1} onChange={setT1} testid="cmp-team1" placeholder="Pick team 1..." />
        <Swords size={16} className="text-[#a855f7]" />
        <TeamSelect teams={teams} value={t2} onChange={setT2} testid="cmp-team2" placeholder="Pick team 2..." />
        <select value={season} onChange={(e) => setSeason(e.target.value)} data-testid="cmp-season"
          className="bg-black/40 border border-white/15 rounded-lg px-3 py-2.5 text-sm focus:border-[#a855f7]/60 focus:outline-none">
          <option value="">This Season</option>
          {YEARS.map((y) => <option key={y} value={y}>{seasonLabel(y)}</option>)}
        </select>
        <button onClick={() => t1 && t2 && setGo({ t1, t2, season })} disabled={!t1 || !t2} data-testid="cmp-run"
          className="bg-[#a855f7] hover:bg-[#9333ea] disabled:opacity-40 text-black font-black uppercase tracking-wider text-xs px-5 py-2.5 rounded transition-colors duration-200">
          Compare
        </button>
      </div>
      {isFetching && <div className="text-neutral-500 font-data text-sm animate-pulse">Pulling {go?.season || "current"} season books...</div>}
      {cmp?.teams && !isFetching && (
        <div className="grid lg:grid-cols-2 gap-4 fade-up-1" data-testid="cmp-result">
          <TeamCard t={cmp.teams[0]} other={cmp.teams[1]} />
          <TeamCard t={cmp.teams[1]} other={cmp.teams[0]} />
        </div>
      )}
      <HeadToHead league={league} comp={comp} go={go} />
      {league !== "soccer" && <PlayerCompare league={league} />}
    </div>
  );
}
